import { useTranslation } from "@/packages/utils";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui";
import { CheckoutLink, CustomerPortalLink } from "@convex-dev/polar/react";
import { api } from "convex/_generated/api";
import { POLAR_PRODUCT_IDS } from "@/packages/features-billing";
import { PLAN_LIMITS } from "@/packages/domains/billing";
import type { BillingTier } from "@/packages/domains/billing";
import type { PlanFeature } from "./types";

interface PricingTableProps {
  currentTier: BillingTier;
}

interface PlanDisplay {
  id: BillingTier;
  name: string;
  price: number;
  description: string;
}

const PLANS: PlanDisplay[] = [
  {
    id: "free",
    name: "Free",
    price: 0,
    description: "For individuals getting started",
  },
  {
    id: "pro",
    name: "Professional",
    price: 29,
    description: "For growing teams",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 99,
    description: "For large organizations",
  },
];

const FEATURES: PlanFeature[] = [
  {
    name: "Audits",
    free: String(PLAN_LIMITS.free.maxAudits),
    pro: "Unlimited",
    enterprise: "Unlimited",
  },
  {
    name: "Team members",
    free: String(PLAN_LIMITS.free.maxMembers),
    pro: String(PLAN_LIMITS.pro.maxMembers),
    enterprise: "Unlimited",
  },
  {
    name: "Document storage",
    free: `${PLAN_LIMITS.free.maxStorageMb} MB`,
    pro: `${Math.round((PLAN_LIMITS.pro.maxStorageMb as number) / 1024)} GB`,
    enterprise: "Unlimited",
  },
  { name: "Audit templates", free: false, pro: true, enterprise: true },
  { name: "API access", free: false, pro: false, enterprise: true },
  { name: "Priority support", free: false, pro: true, enterprise: true },
];

const TIER_ORDER: Record<BillingTier, number> = {
  free: 0,
  pro: 1,
  enterprise: 2,
};

export function PricingTable({ currentTier }: PricingTableProps) {
  const { t } = useTranslation(["settings"]);

  return (
    <div className="border border-border rounded-card overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-page">
            <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-3 w-[200px]">
              Feature
            </th>
            {PLANS.map((plan) => (
              <th
                key={plan.id}
                className="text-center text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-3"
              >
                <span className="block">{plan.name}</span>
                <span className="block text-[13px] font-semibold text-primary normal-case mt-0.5">
                  {plan.price === 0
                    ? t("settings:billing.pricing.free")
                    : `$${plan.price}/mo`}
                </span>
                {plan.id === currentTier && (
                  <span className="inline-block mt-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-medium text-accent">
                    {t("settings:billing.pricing.current")}
                  </span>
                )}
                <div className="mt-2">
                  <PlanAction planId={plan.id} currentTier={currentTier} />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FEATURES.map((feature) => (
            <tr
              key={feature.name}
              className="border-b border-border last:border-b-0 hover:bg-card-hover/30 transition-colors"
            >
              <td className="px-4 py-2.5 text-[13px] text-primary">
                {feature.name}
              </td>
              <FeatureCell value={feature.free} />
              <FeatureCell value={feature.pro} />
              <FeatureCell value={feature.enterprise} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanAction({
  planId,
  currentTier,
}: {
  planId: BillingTier;
  currentTier: BillingTier;
}) {
  const { t } = useTranslation(["settings"]);

  if (planId === currentTier) {
    return null;
  }

  const isUpgrade = TIER_ORDER[planId] > TIER_ORDER[currentTier];

  if (isUpgrade && planId !== "free") {
    const productId =
      POLAR_PRODUCT_IDS[planId as keyof typeof POLAR_PRODUCT_IDS];

    if (!productId) return null;

    return (
      <CheckoutLink polarApi={api.polar} productIds={[productId]}>
        <Button variant="primary" size="sm">
          {t("settings:billing.pricing.upgrade")}
        </Button>
      </CheckoutLink>
    );
  }

  // Downgrade — link to customer portal
  return (
    <CustomerPortalLink
      polarApi={{
        generateCustomerPortalUrl: api.polar.generateCustomerPortalUrl,
      }}
    >
      <Button variant="secondary" size="sm">
        {t("settings:billing.manageSubscription")}
      </Button>
    </CustomerPortalLink>
  );
}

function FeatureCell({ value }: { value: string | boolean }) {
  if (typeof value === "boolean") {
    return (
      <td className="px-4 py-2.5 text-center">
        {value ? (
          <Check size={16} className="inline-block text-accent" />
        ) : (
          <X size={16} className="inline-block text-muted" />
        )}
      </td>
    );
  }

  return (
    <td className="px-4 py-2.5 text-center text-[13px] text-secondary">
      {value}
    </td>
  );
}
